import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus, Search, Filter, BarChart3, X, LogIn, Check, Moon, Sun } from 'lucide-react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  PieController,
  ArcElement 
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import ManagementDashboard from './ManagementDashboard';
import { Session } from '@supabase/supabase-js';
import Landing from './landing';  // Add this import at the top
import FloatingButton from './components/FloatingButton';
import ChatWindow from './components/ChatWindow';

// Register the new chart types
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  PieController,
  ArcElement
);

type Lead = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  description: string;
  created_at: string;
  converted: boolean;
};

type NewLead = Omit<Lead, 'id' | 'created_at'>;
type Theme = 'light' | 'dark';

type AuthPageProps = {
  onAuthSuccess: () => void;
  setIsManagement: React.Dispatch<React.SetStateAction<boolean>>;
  setSession: React.Dispatch<React.SetStateAction<any>>;
};

function AuthPage({ onAuthSuccess, setIsManagement, setSession }: AuthPageProps) {
  // Determine which view is active.
  const [authMode, setAuthMode] = useState<
    'default' | 'signIn' | 'signUp' | 'managementSignIn' | 'managementSignUp'
  >('default');

  // Common input state.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organization, setOrganization] = useState('');

  // Reset state and return to the initial button view.
  const handleCancel = () => {
    setAuthMode('default');
    setEmail('');
    setPassword('');
    setOrganization('');
  };

  // Regular sign in
  const handleSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Regular sign in triggered');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      alert("Sign In Error: " + error.message);
    } else {
      setIsManagement(false);
      setSession(data.session);
      onAuthSuccess();
    }
  };

  // Regular sign up
  const handleSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      // First create the auth user
      const { data, error: signUpError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            is_management: false
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!data?.user?.id) throw new Error('User creation failed - no user ID returned');

      // Then create their organization entry
      const { error: orgError } = await supabase
        .from('user_organization')
        .insert({
          user_id: data.user.id,
          organization: 'default-org'
        });

      if (orgError) throw orgError;
      onAuthSuccess();
    } catch (error) {
      alert("Sign Up Error: " + (error as Error).message);
    }
  };

  // Management sign in with verification
  const handleManagementSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Management sign in triggered');
    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (signInError) throw signInError;

      const { data: managementData, error: managementError } = await supabase
        .from('management_users')
        .select('user_id')
        .eq('user_id', signInData.user.id);

      if (managementError) throw managementError;

      if (!managementData || managementData.length === 0) {
        await supabase.auth.signOut();
        alert("Access denied: Management access required");
        return;
      }

      console.log('Setting management to true');
      setIsManagement(true);
      setSession(signInData.session);
      onAuthSuccess();

    } catch (error) {
      await supabase.auth.signOut();
      alert("Management Sign In Error: " + (error as Error).message);
    }
  };

  // Management sign up with organization
  const handleManagementSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      // First create the user
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password
      });

      if (signUpError) throw signUpError;

      if (!data?.user?.id) {
        throw new Error('User creation failed - no user ID returned');
      }

      // Add their ID and organization to management_users table
      const { error: managementError } = await supabase
        .from('management_users')
        .insert({
          user_id: data.user.id,
          organization: organization
        });

      if (managementError) throw managementError;

      // Set management and session states to route to ManagementDashboard
      setIsManagement(true);
      setSession(data.session);
      onAuthSuccess();
      handleCancel();

    } catch (error) {
      console.error('Signup error:', error);
      alert("Management Sign Up Error: " + (error as Error).message);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-4">
      {authMode === 'default' && (
        <>
          {/* Regular Sign In / Sign Up Buttons */}
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => setAuthMode('signIn')}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md"
            >
              Sign In
            </button>
            <button
              onClick={() => setAuthMode('signUp')}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md"
            >
              Sign Up
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="mx-4 text-gray-500">OR</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          {/* Management Sign In / Sign Up Buttons */}
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => setAuthMode('managementSignIn')}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md"
            >
              Management Sign In
            </button>
            <button
              onClick={() => setAuthMode('managementSignUp')}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md"
            >
              Management Sign Up
            </button>
          </div>
        </>
      )}

      {(authMode === 'signIn' || authMode === 'managementSignIn') && (
        <div>
          <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
            {authMode === 'signIn' ? 'Sign In' : 'Management Sign In'}
          </h2>
          <form 
            onSubmit={(e) => {
              console.log('Form submitted with authMode:', authMode);
              if (authMode === 'signIn') {
                handleSignInSubmit(e);
              } else {
                handleManagementSignInSubmit(e);
              }
            }} 
            className="mt-4"
          >
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <button
              type="submit"
              className={`w-full px-4 py-2 ${
                authMode === 'signIn' ? 'bg-indigo-600' : 'bg-green-600'
              } text-white rounded-md`}
            >
              {authMode === 'signIn' ? 'Sign In' : 'Management Sign In'}
            </button>
          </form>
          <button onClick={handleCancel} className="mt-4 text-red-600">
            Cancel
          </button>
        </div>
      )}

      {authMode === 'signUp' && (
        <div>
          <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
            Sign Up
          </h2>
          <form onSubmit={handleSignUpSubmit} className="mt-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md"
            >
              Sign Up
            </button>
          </form>
          <button onClick={handleCancel} className="mt-4 text-red-600">
            Cancel
          </button>
        </div>
      )}

      {authMode === 'managementSignUp' && (
        <div>
          <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
            Management Sign Up
          </h2>
          <form onSubmit={handleManagementSignUpSubmit} className="mt-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <input
              type="text"
              placeholder="Organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              className="border p-2 w-full mb-2 rounded-md"
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md"
            >
              Sign Up
            </button>
          </form>
          <button onClick={handleCancel} className="mt-4 text-red-600">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLead, setNewLead] = useState<NewLead>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    status: 'new',
    description: '',
    converted: false
  });
  const [error, setError] = useState('');
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: ''
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState(['all', 'new', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed']);
  const [filterOptions, setFilterOptions] = useState({
    status: 'all',
    name: '',
    description: '',
    dateRange: 'all',
    converted: 'all' // 'all', 'converted', 'not_converted'
  });
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDetailEditMode, setIsDetailEditMode] = useState(false);
  const [editingDetailLead, setEditingDetailLead] = useState<Lead | null>(null);
  const [isConversionChartOpen, setIsConversionChartOpen] = useState(false);
  const [isNewLeadsChartOpen, setIsNewLeadsChartOpen] = useState(false);
  const [isTotalLeadsChartOpen, setIsTotalLeadsChartOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>('light');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isManagement, setIsManagement] = useState(false);
  const [organization, setOrganization] = useState('');
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isDelegate, setIsDelegate] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  // Add a new useEffect to fetch leads when session changes
  useEffect(() => {
    if (session) {
      fetchLeads();
    }
  }, [session]);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }
      
      setSession(session);
      setIsAuthenticated(true); // Add this line to set authentication state
      
      // Check if user is management
      const { data: managementData } = await supabase
        .from('management_users')
        .select('user_id')
        .eq('user_id', session.user.id)
        .single();
      
      setIsManagement(!!managementData);
      
      // Check if user is a delegate
      const { data: delegateData } = await supabase
        .from('user_organization')
        .select('is_delegate')
        .eq('user_id', session.user.id)
        .single();
      
      setIsDelegate(delegateData?.is_delegate || false);
      
      setAuthLoading(false);
      
      // Add this line to fetch leads after successful auth
      await fetchLeads();
    } catch (error) {
      console.error('Error checking auth status:', error);
      navigate('/auth');
    }
  };

  useEffect(() => {
    // Check for saved theme preference, default to 'light' if none found
    const savedTheme = localStorage.getItem('theme') as Theme || 'light';
    
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Fetch leads where visible_to matches user's ID or is null (visible to all)
      const { data: fetchedLeads, error } = await supabase
        .from('leads')
        .select('*')
        .or(`visible_to.eq.${user.id},visible_to.is.null`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeads(fetchedLeads || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setError('Failed to fetch leads');
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewLead(prev => ({ ...prev, [name]: value }));
  };

  const handleAuthInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAuthForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: authForm.email,
        password: authForm.password,
      });
      if (error) throw error;
    } catch (error) {
      setAuthError('Failed to sign in. Please check your credentials.');
      console.error('Error signing in:', error);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    try {
      const { error } = await supabase.auth.signUp({
        email: authForm.email,
        password: authForm.password,
      });
      if (error) throw error;
      setAuthError('Check your email for the confirmation link.');
    } catch (error) {
      setAuthError('Failed to sign up. Please try again.');
      console.error('Error signing up:', error);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No authenticated user found');

      // Get user's organization first
      const { data: userData, error: orgError } = await supabase
        .from('user_organization')
        .select('organization')
        .eq('user_id', user.id)
        .single();

      if (orgError) throw orgError;
      if (!userData?.organization) throw new Error('No organization found for user');

      const { error } = await supabase
        .from('leads')
        .insert([{ 
          ...newLead,
          visible_to: user.id,
          user_id: user.id,
          organization: userData.organization // Set the organization from user_organization table
        }]);

      if (error) throw error;
      
      await fetchLeads();
      setIsModalOpen(false);
      setNewLead({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        status: 'new',
        description: '',
        converted: false
      });
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Error creating lead: ' + (error as Error).message);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      const { error } = await supabase
        .from('leads')
        .update({
          first_name: editingLead.first_name,
          last_name: editingLead.last_name,
          email: editingLead.email,
          phone: editingLead.phone,
          company: editingLead.company,
          status: editingLead.status,
          description: editingLead.description,
          // Don't update visible_to field to maintain original visibility
        })
        .eq('id', editingLead.id)
        .eq('visible_to', user.id); // Only allow editing if user has visibility

      if (error) throw error;

      await fetchLeads();
      setIsEditModalOpen(false);
      setEditingLead(null);
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead: ' + (error as Error).message);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditingLead(prev => prev ? { ...prev, [name]: value } : null);
  };

  const filteredLeads = leads.filter(lead => {
    // First apply the search term filter
    const matchesSearch = searchTerm === '' || 
      `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Then apply the existing filters
    const matchesName = filterOptions.name === '' || 
      `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(filterOptions.name.toLowerCase());
    
    const matchesDescription = filterOptions.description === '' ||
      (lead.description && lead.description.toLowerCase().includes(filterOptions.description.toLowerCase()));
    
    const matchesStatus = filterOptions.status === 'all' || lead.status === filterOptions.status;
    
    const matchesConverted = filterOptions.converted === 'all' || 
      (filterOptions.converted === 'converted' ? lead.converted : !lead.converted);
    
    const matchesDate = (() => {
      if (filterOptions.dateRange === 'all') return true;
      
      const leadDate = new Date(lead.created_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      switch (filterOptions.dateRange) {
        case 'today':
          return leadDate >= today;
        case 'week': {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return leadDate >= weekAgo;
        }
        case 'month': {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return leadDate >= monthAgo;
        }
        default:
          return true;
      }
    })();

    return matchesSearch && matchesName && matchesDescription && matchesStatus && matchesConverted && matchesDate;
  });

  const getNewLeadsThisWeek = (leads: Lead[]) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    return leads.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate >= oneWeekAgo;
    }).length;
  };

  const getConversionRate = (leads: Lead[]) => {
    if (leads.length === 0) return 0;
    const convertedLeads = leads.filter(lead => lead.converted).length;
    return Math.round((convertedLeads / leads.length) * 100);
  };

  const getMonthlyConversionRates = () => {
    // Group leads by month (format: "YYYY-MM")
    const monthlyStats: Record<string, { total: number; converted: number }> = {};

    leads.forEach((lead) => {
      const createdDate = new Date(lead.created_at);
      const monthKey = `${createdDate.getFullYear()}-${(createdDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}`;
      if (!monthlyStats[monthKey]) {
        monthlyStats[monthKey] = { total: 0, converted: 0 };
      }
      monthlyStats[monthKey].total++;
      if (lead.converted) {
        monthlyStats[monthKey].converted++;
      }
    });

    // Generate month keys for the current month and the six months prior
    const monthsToDisplay: string[] = [];
    const currentDate = new Date();
    // Normalize to the first day of the current month
    currentDate.setDate(1);

    for (let i = 6; i >= 0; i--) {
      const dt = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const key = `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
      monthsToDisplay.push(key);
    }

    // Map the generated months to conversion rate data. If no data exists for a month, use zero.
    return monthsToDisplay.map((month) => {
      const stat = monthlyStats[month] || { total: 0, converted: 0 };
      const rate = stat.total > 0 ? ((stat.converted / stat.total) * 100).toFixed(0) : "0";
      return { month, rate: Number(rate) };
    });
  };

  const getWeeklyNewLeads = () => {
    // Create an array of the last 12 weeks
    const weeks = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of day
    
    // Start from the Monday of the current week
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(currentMonday);
      weekStart.setDate(currentMonday.getDate() - (7 * i));
      weeks.push(weekStart.toISOString().split('T')[0]);
    }
    
    // Initialize data for all weeks with 0
    const weeklyData = Object.fromEntries(weeks.map(week => [week, 0]));
    
    // Fill in actual lead counts
    leads.forEach(lead => {
      const leadDate = new Date(lead.created_at);
      leadDate.setHours(0, 0, 0, 0); // Normalize lead date to start of day
      
      // Find the Monday of the lead's week
      const leadMonday = new Date(leadDate);
      leadMonday.setDate(leadDate.getDate() - leadDate.getDay() + (leadDate.getDay() === 0 ? -6 : 1));
      const weekKey = leadMonday.toISOString().split('T')[0];
      
      if (weekKey in weeklyData) {
        weeklyData[weekKey]++;
      }
    });

    // Convert to array format
    return Object.entries(weeklyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, count]) => ({
        week,
        count
      }));
  };

  const getCurrentMonthConversionRate = (leads: any[]) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // Zero-based
    const currentYear = currentDate.getFullYear();

    const currentMonthLeads = leads.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate.getMonth() === currentMonth && leadDate.getFullYear() === currentYear;
    });

    const total = currentMonthLeads.length;
    const converted = currentMonthLeads.filter(lead => lead.converted).length;

    return total > 0 ? Math.round((converted / total) * 100) : 0;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isFilterOpen && !target.closest('.filter-button') && !target.closest('.filter-dropdown')) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const handleDeleteLead = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;
      await fetchLeads();
      setSelectedLead(null);
      setIsDetailEditMode(false);
      setEditingDetailLead(null);
      setIsEditModalOpen(false);
      setEditingLead(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  useEffect(() => {
    // Log state changes to debug routing
    console.log('Auth State Changed:', { session: !!session, isManagement });
  }, [session, isManagement]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear session and management state
      setSession(null);
      setIsManagement(false);
      
      // Clear any local storage items we've set
      localStorage.removeItem('theme');
      localStorage.removeItem('darkMode');
      
      // Force reload the page to clear any persisted state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out: ' + (error as Error).message);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  // Management user - show management dashboard
  if (session && isManagement) {
    console.log('Routing to Management Dashboard');
    return <ManagementDashboard />;
  }

  // Regular user - show main app
  return (
    <div className={`min-h-screen bg-white dark:bg-gray-900 ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
              <div className="flex-shrink-0 flex items-center px-4">
                <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">EZ-CRM</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <Sun className="h-5 w-5 text-gray-400" />
                )}
              </button>
              <div className="flex space-x-2">
                {isDelegate && (
                  <button
                    onClick={() => setIsManageModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Manage Leads
                  </button>
                )}
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add Lead
                </button>
              </div>
              <button
                onClick={handleSignOut}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Add Lead Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                Add New Lead
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleLeadSubmit}>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id="first_name"
                      required
                      value={newLead.first_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="last_name"
                      required
                      value={newLead.last_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={newLead.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={newLead.phone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      id="company"
                      value={newLead.company}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={newLead.description}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mr-2 px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Save Lead
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingLead && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Edit Lead</h2>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingLead(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleEditSubmit}>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id="edit_first_name"
                      required
                      value={editingLead.first_name}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="edit_last_name"
                      required
                      value={editingLead.last_name}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="edit_email"
                      value={editingLead.email}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      id="edit_company"
                      value={editingLead.company}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Status
                    </label>
                    <select
                      name="status"
                      id="edit_status"
                      value={editingLead.status}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="edit_description"
                      rows={3}
                      value={editingLead.description}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="edit_phone"
                      value={editingLead.phone}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteLead(editingLead?.id || '')}
                    className="ml-2 px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Delete Lead
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingLead(null);
                    }}
                    className="ml-2 px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                {isDetailEditMode ? 'Edit Lead' : 'Lead Details'}
              </h2>
              <button
                onClick={() => {
                  setSelectedLead(null);
                  setIsDetailEditMode(false);
                  setEditingDetailLead(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!editingDetailLead) return;

                  try {
                    const { error } = await supabase
                      .from('leads')
                      .update({
                        first_name: editingDetailLead.first_name,
                        last_name: editingDetailLead.last_name,
                        email: editingDetailLead.email,
                        phone: editingDetailLead.phone,
                        company: editingDetailLead.company,
                        status: editingDetailLead.status,
                        description: editingDetailLead.description
                      })
                      .eq('id', editingDetailLead.id);

                    if (error) throw error;
                    await fetchLeads();
                    setSelectedLead(editingDetailLead);
                    setIsDetailEditMode(false);
                    setEditingDetailLead(null);
                  } catch (error) {
                    console.error('Error updating lead:', error);
                  }
                }}
              >
                <div className="grid grid-cols-2 gap-6">
                  {isDetailEditMode ? (
                    <>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">First Name</label>
                        <input
                          type="text"
                          value={editingDetailLead?.first_name || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, first_name: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Last Name</label>
                        <input
                          type="text"
                          value={editingDetailLead?.last_name || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, last_name: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Email</label>
                        <input
                          type="email"
                          value={editingDetailLead?.email || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, email: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Company</label>
                        <input
                          type="text"
                          value={editingDetailLead?.company || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, company: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Status</label>
                        <select
                          value={editingDetailLead?.status || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, status: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        >
                          {statusOptions.filter(status => status !== 'all').map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Created</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {new Date(selectedLead.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400">Phone</label>
                        <input
                          type="tel"
                          value={editingDetailLead?.phone || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, phone: e.target.value } : null)}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</h3>
                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-400">{`${selectedLead.first_name} ${selectedLead.last_name}`}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</h3>
                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-400">{selectedLead.email || '-'}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Company</h3>
                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-400">{selectedLead.company || '-'}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</h3>
                        <span className={`mt-1 inline-flex px-2 text-xs leading-5 font-semibold rounded-full ${
                          selectedLead.status === 'new' ? 'bg-green-100 text-green-800' :
                          selectedLead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedLead.status.charAt(0).toUpperCase() + selectedLead.status.slice(1)}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</h3>
                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-400">
                          {new Date(selectedLead.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</h3>
                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-400">{selectedLead.phone || '-'}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Converted</h3>
                        <div className="mt-1">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { error } = await supabase
                                  .from('leads')
                                  .update({ converted: !selectedLead.converted })
                                  .eq('id', selectedLead.id);
                                
                                if (error) throw error;
                                await fetchLeads();
                                setSelectedLead(prev => prev ? { ...prev, converted: !prev.converted } : null);
                              } catch (error) {
                                console.error('Error updating conversion status:', error);
                              }
                            }}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                              selectedLead.converted 
                                ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {selectedLead.converted && <Check className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400">Description</label>
                  {isDetailEditMode ? (
                    <textarea
                      value={editingDetailLead?.description || ''}
                      onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-400 whitespace-pre-wrap">
                      {selectedLead.description || '-'}
                    </p>
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  {isDetailEditMode ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleDeleteLead(editingDetailLead?.id || '')}
                        className="mr-2 px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Delete Lead
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDetailEditMode(false);
                          setEditingDetailLead(null);
                        }}
                        className="mr-2 px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Save Changes
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDetailEditMode(true);
                          setEditingDetailLead(selectedLead);
                        }}
                        className="mr-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Edit Lead
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLead(null);
                          setIsDetailEditMode(false);
                          setEditingDetailLead(null);
                        }}
                        className="mr-2 px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="mb-8 flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative">
            <button 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="filter-button inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Filter className="h-5 w-5 mr-2 text-gray-400" />
              Filter
              {(filterOptions.status !== 'all' || filterOptions.name || filterOptions.description || filterOptions.dateRange !== 'all') && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Active
                </span>
              )}
            </button>

            {/* Enhanced Filter Dropdown */}
            {isFilterOpen && (
              <div className="filter-dropdown origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filterOptions.status}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    >
                      <option value="all">All Statuses</option>
                      {statusOptions.filter(status => status !== 'all').map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Conversion Status</label>
                    <select
                      value={filterOptions.converted}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, converted: e.target.value }))}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    >
                      <option value="all">All Leads</option>
                      <option value="converted">Converted</option>
                      <option value="not_converted">Not Converted</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Contains</label>
                    <input
                      type="text"
                      value={filterOptions.name}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                      placeholder="Filter by name..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description Contains</label>
                    <input
                      type="text"
                      value={filterOptions.description}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                      placeholder="Filter by description..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
                    <select
                      value={filterOptions.dateRange}
                      onChange={(e) => setFilterOptions(prev => ({ ...prev, dateRange: e.target.value }))}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Past Week</option>
                      <option value="month">Past Month</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-2 pt-2 border-t">
                    <button
                      onClick={() => {
                        setFilterOptions({
                          status: 'all',
                          name: '',
                          description: '',
                          dateRange: 'all',
                          converted: 'all'
                        });
                      }}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setIsFilterOpen(false)}
                      className="px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[
            { 
              label: 'Total Leads', 
              value: leads.length, 
              color: 'bg-blue-500',
              onClick: () => setIsTotalLeadsChartOpen(true)
            },
            { 
              label: 'New This Week', 
              value: getNewLeadsThisWeek(leads), 
              color: 'bg-green-500',
              onClick: () => setIsNewLeadsChartOpen(true)
            },
            { 
              label: 'Conversion Rate', 
              value: `${getCurrentMonthConversionRate(leads)}%`, 
              color: 'bg-purple-500',
              onClick: () => setIsConversionChartOpen(true)
            },
            { label: 'Active Deals', value: leads.filter(lead => !lead.converted && lead.status !== 'closed').length, color: 'bg-yellow-500' }
          ].map((stat) => (
            <div 
              key={stat.label} 
              className={`bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg ${stat.onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''}`}
              onClick={stat.onClick}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BarChart3 className={`h-6 w-6 ${stat.color} text-white rounded p-1`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.label}</dt>
                      <dd className="text-lg font-semibold text-gray-900 dark:text-white">{stat.value}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Leads Table */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
          {/* For larger screens */}
          <div className="hidden sm:block">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email/Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Converted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading leads...
                    </td>
                  </tr>
                ) : filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-gray-50 cursor-pointer" 
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{`${lead.first_name} ${lead.last_name}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{lead.email}</div>
                        <div className="text-sm text-gray-500">{lead.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.company || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          lead.status === 'new' ? 'bg-green-100 text-green-800' :
                          lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const { error } = await supabase
                                .from('leads')
                                .update({ converted: !lead.converted })
                                .eq('id', lead.id);
                              
                              if (error) throw error;
                              await fetchLeads();
                            } catch (error) {
                              console.error('Error updating conversion status:', error);
                            }
                          }}
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                            lead.converted 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {lead.converted && <Check className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="max-w-xs truncate">{lead.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingLead(lead);
                            setIsEditModalOpen(true);
                          }} 
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* For mobile screens */}
          <div className="sm:hidden">
            {loading ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">
                Loading leads...
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">
                No leads found
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <li 
                    key={lead.id} 
                    className="px-4 py-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-500">Name:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {`${lead.first_name} ${lead.last_name}`}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs font-medium text-gray-500">Email:</span>
                            <span className="text-sm text-gray-500">{lead.email}</span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs font-medium text-gray-500">Phone:</span>
                            <span className="text-sm text-gray-500">{lead.phone || '-'}</span>
                          </div>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingLead(lead);
                            setIsEditModalOpen(true);
                          }} 
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium ml-4"
                        >
                          Edit
                        </button>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">Company:</span>
                        <span className="text-sm text-gray-500">{lead.company}</span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">Status:</span>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          lead.status === 'new' ? 'bg-green-100 text-green-800' :
                          lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.status}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">Created:</span>
                        <span className="text-sm text-gray-500">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex space-x-2">
                        <span className="text-xs font-medium text-gray-500">Description:</span>
                        <span className="text-sm text-gray-500 flex-1">
                          {lead.description || '-'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-medium text-gray-500">Converted:</span>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const { error } = await supabase
                                .from('leads')
                                .update({ converted: !lead.converted })
                                .eq('id', lead.id);
                              
                              if (error) throw error;
                              await fetchLeads();
                            } catch (error) {
                              console.error('Error updating conversion status:', error);
                            }
                          }}
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                            lead.converted 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {lead.converted && <Check className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>

      {isConversionChartOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                Monthly Conversion Rates
              </h2>
              <button
                onClick={() => setIsConversionChartOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <Bar
                data={{
                  labels: getMonthlyConversionRates().map((d) => {
                    const [year, month] = d.month.split('-');
                    return `${new Date(
                      parseInt(year),
                      parseInt(month) - 1
                    ).toLocaleString('default', { month: 'short' })} ${year}`;
                  }),
                  datasets: [
                    {
                      label: 'Conversion Rate (%)',
                      data: getMonthlyConversionRates().map((d) => d.rate),
                      backgroundColor: 'rgba(147, 51, 234, 0.5)',
                      borderColor: 'rgb(147, 51, 234)',
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top' as const,
                    },
                    title: {
                      display: true,
                      text: 'Monthly Conversion Rates',
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Conversion Rate (%)',
                      },
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Month',
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {isNewLeadsChartOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                Weekly New Leads
              </h2>
              <button
                onClick={() => setIsNewLeadsChartOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <Bar
                data={{
                  labels: getWeeklyNewLeads().map((d) => {
                    const date = new Date(d.week);
                    return `Week of ${date.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`;
                  }),
                  datasets: [
                    {
                      label: 'New Leads',
                      data: getWeeklyNewLeads().map((d) => d.count),
                      backgroundColor: 'rgba(34, 197, 94, 0.5)',
                      borderColor: 'rgb(34, 197, 94)',
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top' as const,
                    },
                    title: {
                      display: true,
                      text: 'Weekly New Leads',
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of New Leads'
                      },
                      ticks: {
                        stepSize: 1
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Week'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {isTotalLeadsChartOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                Lead Conversion Distribution
              </h2>
              <button
                onClick={() => setIsTotalLeadsChartOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="h-[400px] flex items-center justify-center">
                <Pie
                  data={{
                    labels: ['Converted', 'Not Converted'],
                    datasets: [
                      {
                        data: [
                          leads.filter((lead) => lead.converted).length,
                          leads.filter((lead) => !lead.converted).length,
                        ],
                        backgroundColor: [
                          'rgba(34, 197, 94, 0.5)',
                          'rgba(239, 68, 68, 0.5)',
                        ],
                        borderColor: [
                          'rgb(34, 197, 94)',
                          'rgb(239, 68, 68)',
                        ],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                      title: {
                        display: true,
                        text: 'Lead Conversion Distribution',
                        padding: {
                          bottom: 30,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const label = context.label || '';
                            const value = context.raw as number;
                            const total = leads.length;
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${label}: ${value} (${percentage}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {isAuthenticated && (
        <>
          <FloatingButton 
            onClick={() => setIsChatOpen(!isChatOpen)} 
            className="z-50"
          />
          <ChatWindow 
            isOpen={isChatOpen} 
            onClose={() => setIsChatOpen(false)}
          />
        </>
      )}
    </div>
  );
}

export default App;